// E4 doors (47007 W7): pure helpers shared by the new drawers. DISPLAY ONLY: every figure is the server's 2-dp text, nothing is added up here.
import { refusalOf } from '../../../shell/drawer/refusal.mjs';
export { isMissingRoute } from '../closeout/closeoutModel.mjs';

// a coded refusal: the app's reason.<CODE> sentence (EN / FR) when it has one, else the server's own text (error_fr in French), else the fallback. Never a raw e.message.
export function refusalText(data, lang, t, fallback) {
  const e = refusalOf(data, lang);
  return { ...e, text: e.code ? t('reason.' + e.code, { defaultValue: e.text || fallback }) : (e.text || fallback) };
}
export { newKey } from '../../../lib/idempotencyKey.mjs';

// ---- terms edit
export const DAYS_RE = /^\d{1,3}$/;
export const SCOPES = ['unissued', 'future'];
export const termsValid = (f) => DAYS_RE.test(String(f.days || '').trim());
export const termsPreviewBody = (f) => ({ payment_days: Number(String(f.days).trim()), scope: f.scope });
export const termsApplyBody = (f, planHash) => ({ ...termsPreviewBody(f), reason: String(f.reason || '').trim(), plan_hash: planHash });
// the first reason the Apply stays off; null = ready. Blockers come from the served preview.
export function termsBlock(f, pv, isAdmin) {
  if (!isAdmin) return { key: 'ADMIN_ONLY' };
  if (!termsValid(f)) return { key: 'days' };
  if (!pv) return { key: 'loading' };
  if (pv.blockers && pv.blockers.length) return { key: 'blocker', code: pv.blockers[0].code || pv.blockers[0] };
  if (pv.applicable === false) return { key: 'none' };
  if ([...String(f.reason || '').trim()].length < 3) return { key: 'reason' };
  return null;
}

// session flags: a route that answered 404 once is not offered again until the page is reloaded (the door falls back to today's behaviour)
export const FLAGS = { reverse: false };

// who did it: the served { id, name } (or a plain string); '—' when unknown
// 47082: a bare user id is never printed (the Order timeline showed the hash): the API resolves names through the auth directory; an id it could not resolve reads '—'
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const nameOrDash = (v) => (v && !UUID_RE.test(String(v)) ? v : null);
// 47082 (owner): the WHO label of the Audit tab / record sheets: a person's name (or an admin's e-mail), 'System' (+ the job) for a row written by a system job,
// 'Unknown user' for an id nobody resolves; never a dash or a hash
export const whoLabel = (b, t) => {
  if (!b || b.system) return t('e4.who.system', { job: b && b.job ? ` (${String(b.job).replace(/_/g, ' ')})` : '' });
  const n = whoOf(b); return n === '—' ? t('e4.who.unknown') : n;
};
export const whoOf = (b) => (b ? (typeof b === 'string' ? nameOrDash(b) || '—' : nameOrDash(b.name) || nameOrDash(b.email) || '—') : '—');
// 47109 R5g: THE actor resolver of every order-page model row (Adjustments, Lines history, Audit history, Fees decision, header, register lines): the served
// { id, name } (or a plain name / e-mail) -> the name text; an actor id nobody could name (a non-admin viewer, a deleted account) -> UNKNOWN_ACTOR; no actor -> null
// (a system row). Shown through whoLabel: a name, 'Unknown user' or 'System (job)', never a dash or a hash.
export const UNKNOWN_ACTOR = Object.freeze({ unknown: true });
export const actorOf = (u, email) => {
  const n = nameOrDash(u && typeof u === 'object' ? u.name || u.email : u) || nameOrDash(email);
  if (n) return String(n);
  if (!u || (typeof u === 'object' && (u.system || !(u.id || u.name)))) return null;
  return UNKNOWN_ACTOR;
};
// the timeline row -> the drawer it opens (null = a plain row). Delivery rows carry their event key, change rows their record id.
// the timeline row's discriminator: the API sends `kind` (`type` kept as a fallback)
export const rowKind = (r) => (r && (r.kind || r.type)) || null;
export function timelineTarget(r) {
  if (!r) return null;
  const k = rowKind(r);
  if (k === 'delivery') { const ev = r.event || r.event_id || r.id; return ev ? ['delivery_event', ev, { event: ev }] : null; }
  if (k === 'change' && r.id) return ['change_record', r.id, {}];
  return null;
}
// 47127 F11: the cancelled-deposit callout's figure lines. The cancel's move to customer money is DONE (dated): moved / settled on this order / stayed customer money, info tone.
// An older API (no moved_on) keeps its served owed figure. Server text only, nothing added up here.
const zero = (v) => /^-?0*(\.0+)?$/.test(String(v ?? '0'));
export function depositLinesOf(d) {
  const dep = (d && d.deposit) || {};
  if (dep.moved_on && !zero(dep.kept_as_customer_money)) return { tone: 'info', lines: [{ key: 'e4.deposit.moved', p: { moved: dep.kept_as_customer_money, date: dep.moved_on, netted: dep.netted_against_dues || '0.00', left: dep.left_as_customer_money || dep.kept_as_customer_money } }] };
  return { tone: 'warn', lines: d && d.owed_to_customer_from_order != null && !zero(d.owed_to_customer_from_order) ? [{ key: 'e4.deposit.owed', p: { amount: d.owed_to_customer_from_order } }] : [] };
}
